import { useMemo, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays, Repeat, Bell, ChevronLeft, ChevronRight } from "lucide-react";
import { DEMO_CLIENTS } from "../demo/mock-data";
import { monthGrid, dayKey, navigateMonth, monthLabel } from "../demo/calendar-grid";
import { useSyncedState } from "../demo/live/useSyncedState";
import { cn } from "@/lib/utils";

/**
 * DemoCalendar (/calendar) — static mirror of the real Psy180 appointment
 * calendar, shown as a MONTH view (the real app also has week/day). Schedule
 * one-off or recurring sessions, see them laid out on the grid, click a slot to
 * start or cancel, and read how reminders work. All in-memory: adds/cancels reset
 * on reload (no backend, per the isolation guard).
 */

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

interface DemoAppt {
  id: string;
  clientId: string;
  clientName: string;
  startsAt: number; // epoch ms
  durationMin: number;
  recurring: boolean;
}

function fmtTime(ms: number): string {
  return new Date(ms).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}
function formatWhen(ms: number): string {
  return new Date(ms).toLocaleString(undefined, { weekday: "long", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

const DAY = 24 * 60 * 60 * 1000;
function seed(): DemoAppt[] {
  const base = Date.now();
  return [
    { id: "a1", clientId: "c-danna", clientName: "Danna R.", startsAt: base + 2 * DAY, durationMin: 50, recurring: true },
    { id: "a2", clientId: "c-avery", clientName: "Avery Example", startsAt: base + 4 * DAY, durationMin: 50, recurring: false },
    { id: "a3", clientId: "c-danna", clientName: "Danna R.", startsAt: base + 9 * DAY, durationMin: 50, recurring: true },
  ];
}

const card = "rounded-2xl border border-[var(--color-border)] bg-[var(--color-navy-mid)] p-5";
const labelCls = "font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)] block mb-2";
const inputCls =
  "w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-deep)] px-3 py-2 text-sm text-[var(--color-text-primary)] focus:border-[var(--color-gold)] focus:outline-none";

export function DemoCalendar() {
  const navigate = useNavigate();
  const tz = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
  // Co-browsed so the guest follows the presenter through the calendar (the appts
  // list, month, the appointment popover + cancel, and the schedule form). `anchor`
  // rides as epoch-ms because a Date doesn't survive JSON relay. useSyncedState ===
  // useState in the static demo.
  const [appts, setAppts] = useSyncedState<DemoAppt[]>("cal.appts", seed());
  const [anchorMs, setAnchorMs] = useSyncedState<number>("cal.anchorMs", Date.now());
  const anchor = useMemo(() => new Date(anchorMs), [anchorMs]);
  const [selected, setSelected] = useSyncedState<DemoAppt | null>("cal.selected", null);
  const [confirmCancel, setConfirmCancel] = useSyncedState("cal.confirmCancel", false);

  const [clientId, setClientId] = useSyncedState("cal.clientId", DEMO_CLIENTS[0]?.id ?? "");
  const [mode, setMode] = useSyncedState<"one_off" | "recurring">("cal.mode", "one_off");
  const [dateTime, setDateTime] = useSyncedState("cal.dateTime", "");
  const [weekday, setWeekday] = useSyncedState("cal.weekday", 2);
  const [timeOfDay, setTimeOfDay] = useSyncedState("cal.timeOfDay", "15:00");

  const cells = useMemo(() => monthGrid(anchor, tz), [anchor, tz]);
  const byDay = useMemo(() => {
    const m = new Map<string, DemoAppt[]>();
    for (const a of appts) {
      const k = dayKey(new Date(a.startsAt), tz);
      (m.get(k) ?? m.set(k, []).get(k)!).push(a);
    }
    return m;
  }, [appts, tz]);
  const todayKey = dayKey(new Date(), tz);

  function add(e: FormEvent) {
    e.preventDefault();
    const client = DEMO_CLIENTS.find((c) => c.id === clientId);
    if (!client) return;
    let startsAt: number;
    if (mode === "recurring") {
      const [h, m] = timeOfDay.split(":").map(Number);
      const d = new Date();
      d.setHours(h, m, 0, 0);
      while (d.getDay() !== weekday || d.getTime() < Date.now()) d.setDate(d.getDate() + 1);
      startsAt = d.getTime();
    } else {
      if (!dateTime) return;
      startsAt = new Date(dateTime).getTime();
    }
    setAppts((prev) => [...prev, { id: `a${prev.length + 1}-${startsAt}`, clientId, clientName: client.displayName, startsAt, durationMin: 50, recurring: mode === "recurring" }]);
    setDateTime("");
  }

  function selectDay(key: string) {
    setMode("one_off");
    setDateTime(`${key}T09:00`);
  }

  return (
    <main className="mx-auto max-w-4xl px-4 py-8">
      <header className="mb-5">
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--color-gold-soft)]">Calendar</p>
        <h1 className="mt-1 font-heading text-3xl font-medium text-[var(--color-text-primary)]">Appointments</h1>
        <p className="mt-2 text-[13px] leading-relaxed text-[var(--color-text-muted)]">
          Schedule one-off or recurring sessions and see them laid out by month. In this demo, appointments live in memory and reset on reload.
        </p>
      </header>

      {/* Add */}
      <section className={card} data-tour="dc-form">
        <div className="flex items-center gap-2">
          <CalendarDays className="h-4 w-4 text-[var(--color-text-muted)]" />
          <h2 className="font-heading text-[16px] font-medium text-[var(--color-text-primary)]">Schedule an appointment</h2>
        </div>
        <form onSubmit={add} className="mt-4 grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2" data-tour="dc-client">
            <label className={labelCls} htmlFor="dc-client">Client</label>
            <select id="dc-client" value={clientId} onChange={(e) => setClientId(e.target.value)} className={inputCls}>
              {DEMO_CLIENTS.map((c) => (
                <option key={c.id} value={c.id}>{c.displayName}</option>
              ))}
            </select>
          </div>
          <div className="sm:col-span-2 flex gap-2" data-tour="dc-mode">
            {(["one_off", "recurring"] as const).map((mm) => (
              <button
                key={mm}
                type="button"
                onClick={() => setMode(mm)}
                className={cn(
                  "rounded-full border px-4 py-1.5 text-xs transition-colors",
                  mode === mm ? "border-[var(--color-gold)] bg-[var(--color-gold)] text-[var(--color-bg-deep)]" : "border-[var(--color-border)] text-[var(--color-text-muted)] hover:border-[var(--color-gold-soft)]",
                )}
              >
                {mm === "one_off" ? "One-off" : "Recurring (weekly)"}
              </button>
            ))}
          </div>
          {mode === "one_off" ? (
            <div className="sm:col-span-2" data-tour="dc-datetime">
              <label className={labelCls} htmlFor="dc-dt">Date &amp; time</label>
              <input id="dc-dt" type="datetime-local" value={dateTime} onChange={(e) => setDateTime(e.target.value)} className={inputCls} />
            </div>
          ) : (
            <>
              <div data-tour="dc-datetime">
                <label className={labelCls} htmlFor="dc-wd">Repeats every</label>
                <select id="dc-wd" value={weekday} onChange={(e) => setWeekday(Number(e.target.value))} className={inputCls}>
                  {WEEKDAYS.map((d, i) => (
                    <option key={d} value={i}>{d}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls} htmlFor="dc-tod">At</label>
                <input id="dc-tod" type="time" value={timeOfDay} onChange={(e) => setTimeOfDay(e.target.value)} className={inputCls} />
              </div>
            </>
          )}
          <div className="sm:col-span-2">
            <button type="submit" data-tour="dc-add" className="rounded-md bg-[var(--color-gold)] px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-[var(--color-bg-deep)] transition-colors hover:bg-[var(--color-gold-soft)]">
              {mode === "recurring" ? "Add recurring" : "Add appointment"}
            </button>
          </div>
        </form>
      </section>

      {/* Month grid */}
      <section className={cn(card, "mt-4")} data-tour="dc-grid">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-heading text-[16px] font-medium text-[var(--color-text-primary)]">{monthLabel(anchor, tz)}</h2>
          <div className="flex items-center gap-1">
            <button type="button" aria-label="Previous month" onClick={() => setAnchorMs((ms) => navigateMonth(new Date(ms), -1, tz).getTime())} className="rounded-md border border-[var(--color-border)] p-1 text-[var(--color-text-muted)] hover:border-[var(--color-gold-soft)]"><ChevronLeft className="h-4 w-4" /></button>
            <button type="button" onClick={() => setAnchorMs(navigateMonth(anchor, 0, tz).getTime())} className="rounded-md border border-[var(--color-border)] px-2 py-1 font-mono text-[10px] uppercase tracking-wide text-[var(--color-text-muted)] hover:border-[var(--color-gold-soft)]">Today</button>
            <button type="button" aria-label="Next month" onClick={() => setAnchorMs((ms) => navigateMonth(new Date(ms), 1, tz).getTime())} className="rounded-md border border-[var(--color-border)] p-1 text-[var(--color-text-muted)] hover:border-[var(--color-gold-soft)]"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
        <div className="overflow-hidden rounded-xl border border-[var(--color-border)]">
          <div className="grid grid-cols-7 border-b border-[var(--color-border)] bg-[var(--color-bg-deep)]">
            {WEEKDAY_LABELS.map((d) => (
              <div key={d} className="px-1 py-2 text-center font-mono text-[9px] uppercase tracking-[0.16em] text-[var(--color-text-muted)]">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {cells.map((cell) => {
              const key = dayKey(cell.date, tz);
              const dayAppts = byDay.get(key) ?? [];
              const isToday = key === todayKey;
              return (
                <button
                  type="button"
                  key={key}
                  onClick={() => selectDay(key)}
                  className={cn(
                    "min-h-[5.5rem] border-b border-r border-[var(--color-border)] p-1 text-left align-top transition-colors hover:bg-[var(--color-bg-deep)]/60",
                    cell.inMonth ? "bg-transparent" : "bg-[var(--color-bg-deep)]/40",
                  )}
                >
                  <span className={cn(
                    "inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[11px]",
                    isToday ? "bg-[var(--color-gold)] font-semibold text-[var(--color-bg-deep)]" : cell.inMonth ? "text-[var(--color-text-primary)]" : "text-[var(--color-text-ghost)]",
                  )}>
                    {Number(key.slice(8))}
                  </span>
                  <div className="mt-1 space-y-0.5">
                    {dayAppts.slice(0, 2).map((a) => (
                      <span
                        key={a.id}
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); setSelected(a); }}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); setSelected(a); } }}
                        title={`${a.clientName} · ${fmtTime(a.startsAt)}`}
                        className="block truncate rounded bg-[var(--color-gold)]/15 px-1 py-0.5 text-[10px] leading-tight text-[var(--color-text-primary)] hover:bg-[var(--color-gold)]/25"
                      >
                        {a.recurring && "↻ "}{fmtTime(a.startsAt)} {a.clientName}
                      </span>
                    ))}
                    {dayAppts.length > 2 && <span className="block px-1 text-[9px] text-[var(--color-gold-soft)]">+{dayAppts.length - 2} more</span>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
        <p className="mt-2 text-[11px] text-[var(--color-text-ghost)]">Tip: click a day to prefill the form, or click an appointment to start or cancel it.</p>
      </section>

      {/* Reminders — static mirror of the Phase C reminder feature */}
      <section className={cn(card, "mt-4")} data-tour="dc-reminders">
        <div className="flex items-center gap-2">
          <Bell className="h-4 w-4 text-[var(--color-text-muted)]" />
          <h2 className="font-heading text-[16px] font-medium text-[var(--color-text-primary)]">Reminders &amp; notifications</h2>
        </div>
        <p className="mt-2 text-[13px] leading-relaxed text-[var(--color-text-muted)]">
          Each appointment can send reminders — to you and to your client — at the lead times you set
          in <span className="text-[var(--color-text-primary)]">Settings → Account &amp; Notifications</span> (e.g. the
          client 24 hours before, you 15 minutes before). Client reminders are opt-in per client and
          always route safely: a minor&apos;s reminder goes to their guardian. In this demo nothing is actually sent.
        </p>
        <div className="mt-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-deep)] p-4">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-[var(--color-text-ghost)]">Sample client reminder · 24h before</p>
          <p className="mt-2 text-[13px] font-medium text-[var(--color-text-primary)]">Your session is coming up</p>
          <p className="mt-1 text-[12px] leading-relaxed text-[var(--color-text-muted)]">
            Your provider has an upcoming session with you. Open your secure link to join at session time.
          </p>
          <p className="mt-2 font-mono text-[11px] text-[var(--color-gold-soft)]">demo.psy180.com/view/PSK-…</p>
          <p className="mt-3 text-[10px] text-[var(--color-text-ghost)]">No name, no condition — the details stay behind the secure link.</p>
        </div>
      </section>

      {/* Appointment popover. Cancelling is a deliberate two-step "are you sure?";
          dismissing is an explicit Exit button (and the backdrop) — never a cancel. */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => { setSelected(null); setConfirmCancel(false); }}>
          <div className="w-full max-w-sm rounded-2xl border border-[var(--color-border)] bg-[var(--color-navy-mid)] p-5" onClick={(e) => e.stopPropagation()}>
            <p className="text-[14px] font-medium text-[var(--color-text-primary)]">
              {selected.clientName}
              {selected.recurring && (
                <span className="ml-2 inline-flex items-center gap-1 rounded-full border border-[var(--color-border-gold)] px-2 py-0.5 font-mono text-[9px] uppercase tracking-wide text-[var(--color-gold-soft)]">
                  <Repeat className="h-2.5 w-2.5" /> Recurring
                </span>
              )}
            </p>
            <p className="mt-1 text-[12px] text-[var(--color-text-ghost)]">{formatWhen(selected.startsAt)} · {selected.durationMin} min</p>
            {confirmCancel ? (
              <div className="mt-4">
                <p className="text-[13px] leading-relaxed text-[var(--color-text-primary)]">Are you sure you want to cancel this session?</p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button type="button" onClick={() => { setAppts((prev) => prev.filter((x) => x.id !== selected.id)); setSelected(null); setConfirmCancel(false); }} className="rounded-md border border-[var(--color-danger)] px-3 py-1.5 font-mono text-[10px] uppercase tracking-wide text-[var(--color-danger)] transition-colors hover:bg-[var(--color-danger)] hover:text-[var(--color-bg-deep)]">Yes, cancel</button>
                  <button type="button" onClick={() => setConfirmCancel(false)} className="rounded-md bg-[var(--color-gold)] px-3 py-1.5 font-mono text-[10px] uppercase tracking-wide text-[var(--color-bg-deep)] transition-colors hover:bg-[var(--color-gold-soft)]">Keep it</button>
                </div>
              </div>
            ) : (
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <button type="button" onClick={() => navigate(`/session?client=${selected.clientId}`)} className="rounded-md bg-[var(--color-gold)] px-3 py-1.5 font-mono text-[10px] uppercase tracking-wide text-[var(--color-bg-deep)] transition-colors hover:bg-[var(--color-gold-soft)]">Start</button>
                <button type="button" onClick={() => setConfirmCancel(true)} className="rounded-md border border-[var(--color-border)] px-3 py-1.5 font-mono text-[10px] uppercase tracking-wide text-[var(--color-text-muted)] transition-colors hover:border-[var(--color-danger)] hover:text-[var(--color-danger)]">Cancel</button>
                <button type="button" onClick={() => { setSelected(null); setConfirmCancel(false); }} className="ml-auto rounded-md border border-[var(--color-border)] px-3 py-1.5 font-mono text-[10px] uppercase tracking-wide text-[var(--color-text-muted)] transition-colors hover:border-[var(--color-gold-soft)]">Exit</button>
              </div>
            )}
          </div>
        </div>
      )}
    </main>
  );
}
